/**
 * Copyright 2026 Bluecircle Technologies Pvt Limited. All rights reserved.
 *
 * This software and its source code are the proprietary and confidential
 * intellectual property of Bluecircle Technologies Pvt Limited. Unauthorized
 * copying, reproduction, distribution, modification, public display, or use
 * of this software, in whole or in part, is strictly prohibited without the
 * prior written permission of Bluecircle Technologies Pvt Limited.
 *
 * Portions derived from Grist (Copyright Grist Labs), used under the Apache
 * License, Version 2.0. See the NOTICE file for details.
 */

import { AppModel, getHomeUrl } from "app/client/models/AppModel";
import { TelemetryPrefs } from "app/common/Install";
import { InstallAPI, InstallAPIImpl, TelemetryPrefsWithSources } from "app/common/InstallAPI";

import { bundleChanges, Disposable, Observable } from "grainjs";

export interface TelemetryModel {
  /** Telemetry preferences (e.g. the current telemetry level). */
  readonly prefs: Observable<TelemetryPrefsWithSources | null>;
  fetchTelemetryPrefs(): Promise<void>;
  updateTelemetryPrefs(prefs: Partial<TelemetryPrefs>): Promise<void>;
}

export class TelemetryModelImpl extends Disposable implements TelemetryModel {
  public readonly prefs: Observable<TelemetryPrefsWithSources | null> = Observable.create(this, null);
  private readonly _installAPI: InstallAPI = new InstallAPIImpl(getHomeUrl());

  constructor(_appModel: AppModel) {
    super();
  }

  public async fetchTelemetryPrefs(): Promise<void> {
    const prefs = await this._installAPI.getInstallPrefs();
    bundleChanges(() => {
      this.prefs.set(prefs.telemetry);
    });
  }

  public async updateTelemetryPrefs(prefs: Partial<TelemetryPrefs>): Promise<void> {
    await this._installAPI.updateInstallPrefs({ telemetry: prefs });
    await this.fetchTelemetryPrefs();
  }
}
